import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import { fetchData } from "../api/client";
import {
  formatStoredPhoneForInput,
  getPhoneValidationMessage,
  toE164Digits,
  isPhoneSentinel,
  isUsableStoredPhone,
} from "../utils/phone";
import { useCompanies } from "../hooks/useCompanies";
import { useClientTags } from "../hooks/useClientTags";
import CustomFieldsBlock from "./CustomFieldsBlock";
import ClientPortalSection from "./clients/ClientPortalSection";
import ClientFormActionsBar from "./clients/ClientFormActionsBar";
import ClientFormFieldControl from "./clients/ClientFormFieldControl";
import ClientObservacaoField from "./clients/ClientObservacaoField";
import { splitClientObservacao, mergeClientObservacaoForSave } from "../utils/clientObservacao";
import {
  clientFormTabShellSx,
  clientFormTabsSx,
  clientFormTabPanelSx,
  clientFormSubsectionTitleSx,
  clientFormFieldsGridSx,
} from "./clients/clientFormStyles";
import { CLIENT_FORM_TAB_GROUPS, clientFormFieldGridSpan } from "./clients/clientFormTabs";
import { useFieldDefinitions } from "../hooks/useFieldDefinitions";
import { useClientGroups } from "../hooks/useClientGroups";

/** ===== Helpers de transformação =====
 *  Tipo: 1 = PJ, 0 = PF
 */
const TIPO_STR_TO_NUM = (v) => (v === "Pessoa Jurídica" ? 1 : v === "Pessoa Física" ? 0 : null);

const SEXO_NUM_TO_LABEL = { 1: "Masculino", 2: "Feminino", 3: "Outro" };
const SEXO_LABEL_TO_NUM = { Masculino: 1, Feminino: 2, Outro: 3, Outros: 3 };

const ESTADO_CIVIL_NUM_TO_LABEL = {
  1: "Solteiro",
  2: "Casado",
  3: "Divorciado",
  4: "Viúvo",
  5: "Separado",
};
const ESTADO_CIVIL_LABEL_TO_NUM = {
  Solteiro: 1,
  Casado: 2,
  Divorciado: 3,
  Viúvo: 4,
  Separado: 5,
};

const FIELD_LABELS = {
  tipo: "Tipo",
  nome: "Nome",
  apelido: "Apelido",
  data_nascimento: "Data de nascimento",
  sexo: "Sexo",
  cpf: "CPF",
  identidade: "RG / Identidade",
  orgaoemissor: "Órgão emissor",
  naturalidade: "Naturalidade",
  estado_civil: "Estado civil",
  razao_social: "Razão social",
  nome_fantasia: "Nome fantasia",
  cnpj: "CNPJ",
  data_abertura: "Data de abertura",
  inscricao_estadual: "Inscrição estadual",
  inscricao_estadual_isento: "Isento de inscrição estadual",
  nome_responsavel: "Nome do responsável",
  cargo_responsavel: "Cargo do responsável",
  tamanho_frota: "Tamanho da frota",
  email: "E-mail",
  telefone: "Telefone",
  celular: "Celular",
  whatsapp: "WhatsApp",
  cep: "CEP",
  cidade: "Cidade",
  uf: "UF",
  endereco: "Endereço",
  bairro: "Bairro",
  numero: "Número",
  complemento: "Complemento",
  referencia: "Referência",
  id_interno: "ID interno",
  id_grupo: "Nível de consciência",
  score: "Score",
  potencial: "Potencial (código)",
  origem: "Origem",
  profissao: "Profissão",
  cargo: "Cargo",
  website: "Site / URL",
  biografia: "Biografia",
  id_facebook: "Facebook",
  id_instagram: "Instagram",
  id_twitter: "Twitter / X",
  id_linkedin: "LinkedIn",
  id_googleplus: "Google+",
  id_projeto: "Empresa",
  status: "Status",
  tags: "Tags",
  lgpd: "LGPD aceita",
  tiposanguineo: "Tipo sanguíneo",
  preferencia_contato: "Preferência de contato",
  observacao: "Notas internas",
};

const fieldLabel = (field) => {
  const base = FIELD_LABELS[field.name] || field.name.replace(/_/g, " ");
  const text = base.charAt(0).toUpperCase() + base.slice(1);
  return field.required ? `${text} *` : text;
};

const STATUS_STR_TO_NUM = (v) => (v === "Ativo" ? 1 : v === "Inativo" ? 0 : null);

/** Normaliza DATE/DATETIME da API para input type="date" (YYYY-MM-DD). */
const normalizeClientDateForInput = (raw) => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!s || s.startsWith("0000-00-00")) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

// aceita number ou "123" -> 123 | senão null
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Garante que o valor no Select seja SEMPRE o rótulo esperado
const normalizeTipoToLabel = (v, fallback = "Pessoa Física") => {
  const n = toNum(v);
  if (n === 1) return "Pessoa Jurídica"; // 1 = PJ
  if (n === 0) return "Pessoa Física";   // 0 = PF

  const s = String(v ?? "").toLowerCase();
  if (s.includes("jur")) return "Pessoa Jurídica";
  if (s.includes("fís") || s.includes("fis")) return "Pessoa Física";

  return fallback;
};

const normalizeStatusToLabel = (v, fallback = "Ativo") => {
  const n = toNum(v);
  if (n === 1) return "Ativo";
  if (n === 0) return "Inativo";

  const s = String(v ?? "").toLowerCase();
  if (s.includes("inativo")) return "Inativo";
  if (s.includes("ativo")) return "Ativo";

  return fallback;
};

const AddClientForm = ({
  client,
  onClose,
  onSubmit,
  isFetching,
  defaultProjectId = null,
  onDuplicateDetected,
  variant = "embedded",
  formId = "client-form",
  hideFooterActions = false,
  onSaveStateChange,
}) => {
  const isEditMode = Boolean(client && client.id);
  const [formData, setFormData] = useState({
    tipo: "Pessoa Física",
    status: "Ativo",
    lgpd: 0,
    id_projeto: defaultProjectId ?? null,
    tags: [],
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [portalAccessSaving, setPortalAccessSaving] = useState(false);
  const [formMode, setFormMode] = useState(isEditMode ? "complete" : "quick");
  const [activeFormTab, setActiveFormTab] = useState(0);
  const [tagSearch, setTagSearch] = useState("");

  const { data: tagOptions } = useClientTags({ search: tagSearch, limit: 200, includeUsed: false });
  const { data: fieldDefs, isLoading: fieldDefsLoading } = useFieldDefinitions({ enabled: true });
  const [customFieldValues, setCustomFieldValues] = useState({});
  const baselineRef = useRef(null);
  const pendingBaselineRef = useRef(false);
  const observacaoAuditRef = useRef([]);
  const [observacaoAuditLines, setObservacaoAuditLines] = useState([]);

  // Empresas / Projetos (mesma fonte do AddProductForm)
  const {
    data: companiesQuery = { companies: [], pagination: { total: 0 } },
    isLoading: companiesLoading,
  } = useCompanies(1, 500, "");
  const companies = companiesQuery?.companies || [];

  const { data: groupsData, isLoading: groupsLoading } = useClientGroups(1, 200, "");
  const clientGroups = useMemo(() => groupsData?.groups || [], [groupsData?.groups]);

  const isPageVariant = variant === "page";

  // Re-hidrata (normaliza) quando o detalhe chega da API
  useEffect(() => {
    if (!client) return;

    setFormData((prev) => {
      const tipoStr = normalizeTipoToLabel(client.tipo, prev.tipo || "Pessoa Física");
      const statusStr = normalizeStatusToLabel(client.status, prev.status || "Ativo");
      const rawTags = client.tags ?? "";
      const parsedTags =
        typeof rawTags === "string"
          ? rawTags.split(",").map((t) => t.trim()).filter(Boolean)
          : Array.isArray(rawTags)
          ? rawTags.map((t) => String(t).trim()).filter(Boolean)
          : [];

      const sexoNum = toNum(client.sexo);
      const ecNum = toNum(client.estado_civil);
      const { userNotes, auditLines } = splitClientObservacao(client.observacao);
      observacaoAuditRef.current = auditLines;
      setObservacaoAuditLines(auditLines);

      return {
        ...prev,
        ...client,
        observacao: userNotes,
        razao_social: client.razao_social ?? client.razaosocial ?? prev.razao_social ?? "",
        nome_fantasia: (() => {
          const razao = String(
            client.razao_social ?? client.razaosocial ?? ""
          ).trim();
          const nome = String(client.nome ?? "").trim();
          // `nome` guarda o nome fantasia; se estiver vazio ou só espelhar a razão social, o campo fica em branco.
          if (!nome || (razao && nome === razao)) return "";
          return nome;
        })(),
        identidade: client.identidade ?? client.rg ?? prev.identidade ?? "",
        data_abertura: normalizeClientDateForInput(client.data_abertura),
        data_nascimento: normalizeClientDateForInput(client.data_nascimento),
        inscricao_estadual: client.inscricao_estadual ?? "",
        inscricao_estadual_isento: Number(client.inscricao_estadual_isento) ? 1 : 0,
        nome_responsavel: client.nome_responsavel ?? "",
        cargo_responsavel: client.cargo_responsavel ?? "",
        tamanho_frota: client.tamanho_frota ?? "",
        id_linkedin: client.id_linkedin ?? client.linkedin ?? prev.id_linkedin ?? "",
        id_grupo: client.id_grupo ?? prev.id_grupo ?? "",
        score: client.score ?? prev.score ?? "",
        potencial: client.potencial ?? prev.potencial ?? "",
        origem: client.origem ?? prev.origem ?? "",
        tipo: tipoStr,
        status: statusStr,
        sexo: sexoNum != null ? SEXO_NUM_TO_LABEL[sexoNum] || prev.sexo || "" : prev.sexo || "",
        estado_civil:
          ecNum != null ? ESTADO_CIVIL_NUM_TO_LABEL[ecNum] || prev.estado_civil || "" : prev.estado_civil || "",
        tags: parsedTags,
        telefone: client.telefone && isUsableStoredPhone(client.telefone)
          ? formatStoredPhoneForInput(String(client.telefone))
          : prev.telefone ?? "",
        celular: client.celular && isUsableStoredPhone(client.celular)
          ? formatStoredPhoneForInput(String(client.celular))
          : prev.celular ?? "",
        whatsapp: (() => {
          const raw =
            (client.whatsapp && isUsableStoredPhone(client.whatsapp) && String(client.whatsapp)) ||
            (client.numero_whatsapp && isUsableStoredPhone(client.numero_whatsapp) && String(client.numero_whatsapp)) ||
            "";
          return raw ? formatStoredPhoneForInput(raw) : prev.whatsapp ?? "";
        })(),
        acesso_portal_cliente: Number(client.acesso_portal_cliente) ? 1 : 0,
        lgpd: Number(client.lgpd) ? 1 : 0,
      };
    });
    setCustomFieldValues({
      campo_adicional1_sim_nao: client.campo_adicional1_sim_nao ?? 0,
      campo_adicional2_sim_nao: client.campo_adicional2_sim_nao ?? 0,
      campo_adicional3_multiselect: client.campo_adicional3_multiselect ?? "",
      ...(client.customFields || {}),
    });
    if (client.id) {
      pendingBaselineRef.current = true;
    }
  }, [client]);

  useEffect(() => {
    setFormMode(isEditMode ? "complete" : "quick");
    if (!isEditMode) {
      pendingBaselineRef.current = true;
    }
  }, [isEditMode]);

  const sections = [
    {
      title: "Identificação",
      subtitle: "Tipo de pessoa e dados principais usados em solicitações e relatórios.",
      navTitle: "Identificação",
      fields: [
        {
          name: "tipo",
          type: "select",
          required: true,
          options: ["Pessoa Física", "Pessoa Jurídica"],
        },
      ],
    },
    {
      title: "Classificação comercial",
      subtitle: "Referências internas, grupo de consciência e indicadores de relacionamento.",
      navTitle: "Classificação",
      fields: [
        { name: "id_interno", type: "text", required: false },
        { name: "id_grupo", type: "grupo", required: false },
        { name: "score", type: "number", required: false },
        { name: "potencial", type: "number", required: false },
        { name: "origem", type: "text", required: false },
      ],
    },
    {
      title: "Dados pessoais",
      subtitle: "Informações do titular quando o cadastro é pessoa física.",
      navTitle: "Pessoais",
      pfOnly: true,
      fields: [
        { name: "nome", type: "text", required: true },
        { name: "apelido", type: "text", required: false },
        { name: "data_nascimento", type: "date", required: false },
        { name: "sexo", type: "select", required: false, options: ["Masculino", "Feminino", "Outro"] },
        { name: "cpf", type: "text", required: false },
        { name: "estado_civil", type: "select", required: false, options: ["Solteiro", "Casado", "Divorciado", "Viúvo", "Separado"] },
      ],
    },
    {
      title: "Documentos",
      subtitle: "Documento de identidade e naturalidade.",
      navTitle: "Documentos",
      pfOnly: true,
      fields: [
        { name: "identidade", type: "text", required: false },
        { name: "orgaoemissor", type: "text", required: false },
        { name: "naturalidade", type: "text", required: false },
      ],
    },
    {
      title: "Dados empresariais",
      subtitle: "Razão social, CNPJ e demais informações da empresa.",
      navTitle: "Empresa",
      pjOnly: true,
      fields: [
        { name: "razao_social", type: "text", required: true },
        { name: "nome_fantasia", type: "text", required: false },
        { name: "cnpj", type: "text", required: true },
        { name: "data_abertura", type: "date", required: false },
        { name: "inscricao_estadual", type: "text", required: false },
        {
          name: "inscricao_estadual_isento",
          type: "boolean",
          required: false,
          helperText: "Marque se a empresa for isenta de IE.",
        },
        { name: "nome_responsavel", type: "text", required: false },
        { name: "cargo_responsavel", type: "text", required: false },
        { name: "tamanho_frota", type: "text", required: false },
      ],
    },
    {
      title: "Contato",
      subtitle: "Canais para retorno do time e envio de notificações ao cliente.",
      navTitle: "Contato",
      fields: [
        { name: "email", type: "email", required: false },
        { name: "telefone", type: "text", required: false },
        { name: "celular", type: "text", required: false },
        { name: "whatsapp", type: "text", required: false },
        { name: "preferencia_contato", type: "select", required: false, options: ["Telefone", "E-mail", "WhatsApp"] },
      ],
    },
    {
      title: "Perfil",
      subtitle: "Contexto profissional e presença online.",
      navTitle: "Perfil",
      fields: [
        { name: "profissao", type: "text", required: false },
        { name: "cargo", type: "text", required: false },
        { name: "website", type: "text", required: false },
        { name: "biografia", type: "textarea", required: false, fullWidth: true, pfOnly: true },
      ],
    },
    {
      title: "Redes sociais",
      subtitle: "Usuários, URLs ou IDs usados para identificar o cliente nas redes.",
      navTitle: "Redes",
      fields: [
        { name: "id_facebook", type: "text", required: false },
        { name: "id_instagram", type: "text", required: false },
        { name: "id_twitter", type: "text", required: false },
        { name: "id_linkedin", type: "text", required: false },
        { name: "id_googleplus", type: "text", required: false },
      ],
    },
    {
      title: "Endereço",
      subtitle: "Localização principal para entregas, faturamento e correspondência.",
      navTitle: "Endereço",
      fields: [
        { name: "cep", type: "text", required: false },
        { name: "endereco", type: "text", required: false },
        { name: "numero", type: "text", required: false },
        { name: "complemento", type: "text", required: false },
        { name: "bairro", type: "text", required: false },
        { name: "cidade", type: "text", required: false },
        { name: "uf", type: "text", required: false },
        { name: "referencia", type: "text", required: false },
      ],
    },
    {
      title: "Vínculo e status",
      subtitle: "Empresa do workspace, situação do cadastro e tags de segmentação.",
      navTitle: "Vínculo",
      fields: [
        { name: "id_projeto", type: "select", required: false },
        { name: "status", type: "select", required: true, options: ["Ativo", "Inativo"] },
        {
          name: "lgpd",
          type: "boolean",
          required: false,
          fullWidth: true,
          helperText: "Indica que o cliente aceitou o tratamento de dados pessoais conforme a LGPD.",
        },
        { name: "tags", type: "tags", required: false },
        { name: "tiposanguineo", type: "select", required: false, options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], pfOnly: true },
        { name: "observacao", type: "textarea", required: false, fullWidth: true },
      ],
    },
  ];

  const isPessoaFisica = formData.tipo === "Pessoa Física";
  const isPessoaJuridica = formData.tipo === "Pessoa Jurídica";

  const shouldRenderSection = (section) => {
    if (isPessoaFisica && section.pjOnly) return false;
    if (isPessoaJuridica && section.pfOnly) return false;
    if (isPessoaJuridica && section.title === "Dados pessoais") return false;
    if (isPessoaJuridica && section.title === "Documentos") return false;
    if (isPessoaFisica && section.title === "Dados empresariais") return false;
    return true;
  };

  const shouldRenderField = (field) => {
    if (field.pfOnly && !isPessoaFisica) return false;
    if (field.pjOnly && !isPessoaJuridica) return false;
    return true;
  };

  const handleCepChange = async (e) => {
    const cepValue = e.target.value;
    const cep = cepValue.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, cep: cepValue }));

    if (cep.length === 8) {
      try {
        const res = await fetchData(`/cep/lookup?cep=${cep}`, "GET");
        const data = res?.data || res;
        setFormData((prev) => ({
          ...prev,
          cidade: data.city || data.localidade || "",
          uf: data.uf || "",
          endereco: data.logradouro || "",
          bairro: data.bairro || "",
        }));
        setErrors((prev) => ({ ...prev, cep: "" }));
      } catch {
        setErrors((prev) => ({ ...prev, cep: "CEP inválido ou não encontrado" }));
      }
    } else if (cepValue && cepValue.length < 9) {
      setErrors((prev) => ({ ...prev, cep: "CEP inválido" }));
    } else {
      setErrors((prev) => ({ ...prev, cep: "" }));
    }
  };

  const validateField = (name, value) => {
    if (!value) return "";

    if (name === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "E-mail inválido";
    }

    if (name === "cep") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 8) return "CEP inválido";
      return "";
    }

    if (name === "cpf") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 11) return "CPF deve ter 11 dígitos";

      const invalidCpfs = [
        "00000000000",
        "11111111111",
        "22222222222",
        "33333333333",
        "44444444444",
        "55555555555",
        "66666666666",
        "77777777777",
        "88888888888",
        "99999999999",
      ];
      if (invalidCpfs.includes(digits)) return "CPF inválido";

      let sum = 0;
      for (let i = 0; i < 9; i += 1) {
        sum += parseInt(digits.charAt(i), 10) * (10 - i);
      }
      let rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(digits.charAt(9), 10)) return "CPF inválido";

      sum = 0;
      for (let i = 0; i < 10; i += 1) {
        sum += parseInt(digits.charAt(i), 10) * (11 - i);
      }
      rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(digits.charAt(10), 10)) return "CPF inválido";

      return "";
    }

    if (name === "cnpj") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 14) return "CNPJ deve ter 14 dígitos";

      const invalidCnpjs = [
        "00000000000000",
        "11111111111111",
        "22222222222222",
        "33333333333333",
        "44444444444444",
        "55555555555555",
        "66666666666666",
        "77777777777777",
        "88888888888888",
        "99999999999999",
      ];
      if (invalidCnpjs.includes(digits)) return "CNPJ inválido";

      const calcCnpjDigit = (base) => {
        let sum = 0;
        let pos = base.length - 7;
        for (let i = base.length; i >= 1; i -= 1) {
          sum += parseInt(base.charAt(base.length - i), 10) * pos;
          pos -= 1;
          if (pos < 2) pos = 9;
        }
        const result = sum % 11;
        return result < 2 ? 0 : 11 - result;
      };

      const base = digits.substring(0, 12);
      const digit1 = calcCnpjDigit(base);
      const digit2 = calcCnpjDigit(base + digit1);
      if (digits !== base + String(digit1) + String(digit2)) return "CNPJ inválido";

      return "";
    }

    if (name === "uf") {
      const uf = String(value).trim().toUpperCase();
      if (uf && uf.length !== 2) return "UF deve ter 2 letras";
      return "";
    }

    if (name === "numero") {
      const onlyDigits = String(value).replace(/\D/g, "");
      if (!onlyDigits) return "Número inválido";
      return "";
    }

    if (name === "id_linkedin" || name === "website") {
      const trimmed = String(value).trim();
      if (trimmed && /^https?:\/\//i.test(trimmed) && !/^https?:\/\/.+/i.test(trimmed)) {
        return "Informe uma URL válida";
      }
      return "";
    }

    if (name === "identidade") {
      const cleaned = String(value).replace(/[.-]/g, "").trim();
      if (cleaned && cleaned.length < 5) return "Documento inválido";
      return "";
    }

    if (name === "telefone" || name === "celular" || name === "whatsapp") {
      return getPhoneValidationMessage(value, "BR");
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "inscricao_estadual_isento" && Number(value) === 1) {
        next.inscricao_estadual = "";
      }
      return next;
    });
    setErrors((prev) => ({
      ...prev,
      [name]: error,
      ...(name === "inscricao_estadual_isento" && Number(value) === 1
        ? { inscricao_estadual: "" }
        : {}),
    }));
    setApiError(null);
  };

  const handleCompanyChange = (_evt, companyOption) => {
    setFormData((prev) => ({
      ...prev,
      id_projeto: companyOption ? companyOption.id : null,
    }));
    setApiError(null);
  };

  const buildDuplicateCheckQuery = (payload, excludeId = null) => {
    const sp = new URLSearchParams();
    const idProjeto = payload?.id_projeto ?? formData?.id_projeto;
    if (idProjeto) sp.set("id_projeto", String(idProjeto));
    if (payload?.email) sp.set("email", String(payload.email));
    if (payload?.cpf) sp.set("cpf", String(payload.cpf));
    if (payload?.cnpj) sp.set("cnpj", String(payload.cnpj));
    if (payload?.celular) sp.set("celular", String(payload.celular));
    if (payload?.telefone) sp.set("telefone", String(payload.telefone));
    if (payload?.whatsapp) sp.set("whatsapp", String(payload.whatsapp));
    if (payload?.id_interno) sp.set("id_interno", String(payload.id_interno));
    if (excludeId) sp.set("exclude_id", String(excludeId));
    return sp.toString();
  };

  const formatDuplicateError = (duplicates = []) => {
    if (!Array.isArray(duplicates) || duplicates.length === 0) {
      return "Já existe cliente com os dados informados neste projeto.";
    }
    const top = duplicates.slice(0, 3);
    const names = top.map((d) => `#${d.id} ${d.nome || "Sem nome"}`).join(" | ");
    return `Cliente possivelmente duplicado encontrado: ${names}.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validações (obrigatórios + formato)
    const newErrors = {};

    sections.forEach((section) => {
      if (!shouldRenderSection(section)) return;

      section.fields.forEach((field) => {
        if (!shouldRenderField(field)) return;
        const value = formData[field.name];

        if (field.required) {
          const isEmpty =
            value === null ||
            value === undefined ||
            (typeof value === "string" && value.trim() === "");
          if (isEmpty) {
            newErrors[field.name] = "Campo obrigatório";
            return;
          }
        }

        const err = validateField(field.name, value);
        if (err) newErrors[field.name] = err;
      });
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (Number(formData.acesso_portal_cliente) && !String(formData.email || "").trim()) {
      setErrors((prev) => ({
        ...prev,
        email: "E-mail é obrigatório para liberar o portal do cliente.",
      }));
      return;
    }

    // desnormaliza para o payload da API
    const payload = { ...formData };

    // tags (UI usa array) -> string CSV (API usa string)
    if (Array.isArray(payload.tags)) {
      const cleaned = payload.tags.map((t) => String(t || "").trim()).filter(Boolean);
      payload.tags = cleaned.length ? cleaned.join(", ") : "";
    }

    // tipo -> número (tolerante) | 1=PJ 0=PF
    const tipoNum = TIPO_STR_TO_NUM(formData.tipo);
    if (tipoNum !== null) payload.tipo = tipoNum;
    else {
      const n = toNum(formData.tipo);
      if (n === 0 || n === 1) payload.tipo = n;
    }

    // status -> número (tolerante)
    const statusNum = STATUS_STR_TO_NUM(formData.status);
    if (statusNum !== null) payload.status = statusNum;
    else {
      const n = toNum(formData.status);
      if (n === 0 || n === 1) payload.status = n;
    }

    // alinhar nome de campo da razão social com a API
    if (payload.razao_social !== undefined) {
      payload.razaosocial = payload.razao_social;
      delete payload.razao_social;
    }

    // PJ: campo de UI `nome_fantasia` → coluna `clientes.nome` (não existe nome_fantasia no DB).
    // Sem fantasia, usa a razão social como nome de exibição.
    if (tipoNum === 1) {
      const fantasia = String(payload.nome_fantasia ?? "").trim();
      delete payload.nome_fantasia;
      if (fantasia) {
        payload.nome = fantasia;
      } else if (payload.razaosocial) {
        payload.nome = payload.razaosocial;
      }
    } else {
      delete payload.nome_fantasia;
    }

    if (payload.sexo) {
      const sexoN = SEXO_LABEL_TO_NUM[payload.sexo] ?? toNum(payload.sexo);
      if (sexoN != null) payload.sexo = sexoN;
    } else {
      delete payload.sexo;
    }

    if (payload.estado_civil) {
      const ecN = ESTADO_CIVIL_LABEL_TO_NUM[payload.estado_civil] ?? toNum(payload.estado_civil);
      if (ecN != null) payload.estado_civil = ecN;
    } else {
      delete payload.estado_civil;
    }

    if (payload.id_grupo === "" || payload.id_grupo == null) {
      payload.id_grupo = null;
    } else {
      const g = toNum(payload.id_grupo);
      payload.id_grupo = g != null ? g : null;
    }

    ["score", "potencial"].forEach((k) => {
      const raw = payload[k];
      if (raw === "" || raw == null) {
        delete payload[k];
      } else {
        const n = toNum(raw);
        if (n != null) payload[k] = n;
        else delete payload[k];
      }
    });

    delete payload.rg;
    delete payload.linkedin;

    payload.acesso_portal_cliente = Number(formData.acesso_portal_cliente) ? 1 : 0;
    payload.lgpd = Number(formData.lgpd) ? 1 : 0;
    payload.inscricao_estadual_isento = Number(formData.inscricao_estadual_isento) ? 1 : 0;
    if (payload.inscricao_estadual_isento) {
      payload.inscricao_estadual = null;
    } else if (payload.inscricao_estadual != null) {
      payload.inscricao_estadual = String(payload.inscricao_estadual).trim() || null;
    }
    if (payload.data_abertura) {
      payload.data_abertura = String(payload.data_abertura).slice(0, 10);
    } else {
      payload.data_abertura = null;
    }
    ["nome_responsavel", "cargo_responsavel", "tamanho_frota"].forEach((k) => {
      if (payload[k] != null) payload[k] = String(payload[k]).trim() || null;
    });

    payload.observacao = mergeClientObservacaoForSave(
      formData.observacao,
      observacaoAuditRef.current
    );

    ["telefone", "celular", "whatsapp"].forEach((k) => {
      const v = payload[k];
      if (v && String(v).trim() && !isPhoneSentinel(String(v))) {
        const e164 = toE164Digits(v, "BR");
        if (e164) payload[k] = e164;
      }
    });
    // Persistir número em numero_whatsapp (VARCHAR); API também remapeia whatsapp→flag+numero.
    if (payload.whatsapp && isUsableStoredPhone(payload.whatsapp)) {
      payload.numero_whatsapp = payload.whatsapp;
    }

    const eavFields = {};
    for (const def of fieldDefs?.client || []) {
      if (def.visible === false) continue;
      const val = customFieldValues[def.key];
      if (val === undefined) continue;
      if (def.legacy) {
        payload[def.key] = val;
      } else {
        eavFields[def.key] = val;
      }
    }
    if (Object.keys(eavFields).length) {
      payload.customFields = eavFields;
    }

    if (onSubmit) {
      try {
        if (!isEditMode) {
          const query = buildDuplicateCheckQuery(payload);
          if (query.includes("id_projeto=")) {
            const dupResp = await fetchData(`/clients/duplicate-check?${query}`, "GET");
            const dupData = dupResp?.data || dupResp;
            if (dupData?.hasDuplicates) {
              const duplicates = dupData?.duplicates || [];
              if (typeof onDuplicateDetected === "function") {
                onDuplicateDetected({ duplicates, attemptedPayload: payload, phase: "precheck" });
              } else {
                const msg = formatDuplicateError(duplicates);
                setApiError(msg);
              }
              return;
            }
          }
        }

        await onSubmit(payload);
        setApiError(null);
      } catch (err) {
        const unknownTags =
          err?.data?.unknown ||
          err?.unknown ||
          err?.data?.data?.unknown ||
          null;

        // Normaliza mensagem geral
        const backendMsg =
          err?.data?.message ||
          err?.data ||
          err?.message ||
          "Erro ao salvar o cliente.";

        if (Array.isArray(unknownTags) && unknownTags.length) {
          setApiError(`Uma ou mais tags não são permitidas: ${unknownTags.join(", ")}`);
          return;
        }

        if (
          typeof onDuplicateDetected === "function" &&
          (err?.code === "errors.clients.duplicate" || err?.data?.code === "errors.clients.duplicate")
        ) {
          const duplicates = err?.data?.duplicates || err?.data?.data?.duplicates || err?.duplicates || [];
          onDuplicateDetected({ duplicates, attemptedPayload: payload, phase: "submit" });
          return;
        }

        setApiError(
          typeof backendMsg === "string"
            ? backendMsg
            : "Erro ao salvar o cliente. Verifique os campos obrigatórios."
        );

        // Tenta mapear erros para campos específicos (ex.: Column 'email' cannot be null)
        const text = String(
          err?.data?.details || err?.data?.message || err?.data || err?.message || ""
        ).toLowerCase();
        const fieldHints = {
          email: "Houve um erro ao salvar o e-mail. Verifique o valor informado.",
          nome: "Informe o nome do cliente.",
          razaosocial: "Informe a razão social da empresa.",
          razao_social: "Informe a razão social da empresa.",
          cnpj: "Informe o CNPJ da empresa.",
          cpf: "Informe um CPF válido e não duplicado.",
          celular: "Informe um celular válido e não duplicado.",
          telefone: "Informe um telefone válido e não duplicado.",
          whatsapp: "Informe um WhatsApp válido e não duplicado.",
          id_interno: "Este ID interno já está em uso para outro cliente.",
        };

        const fieldErrorsFromApi = {};
        Object.keys(fieldHints).forEach((fieldKey) => {
          if (text.includes(fieldKey)) {
            const mappedKey =
              fieldKey === "razaosocial" ? "razao_social" : fieldKey;
            fieldErrorsFromApi[mappedKey] = fieldHints[fieldKey];
          }
        });

        if (Object.keys(fieldErrorsFromApi).length) {
          setErrors((prev) => ({ ...prev, ...fieldErrorsFromApi }));
        }
      }
    }
  };

  const isSubmitting = Boolean(isFetching);

  const portalRequiresEmail =
    Number(formData.acesso_portal_cliente) === 1 &&
    !String(formData.email || "").trim();

  const serializeFormSnapshot = useCallback(
    () =>
      JSON.stringify({
        formData,
        customFieldValues,
      }),
    [formData, customFieldValues]
  );

  useEffect(() => {
    if (!pendingBaselineRef.current || !isEditMode) return;
    pendingBaselineRef.current = false;
    baselineRef.current = serializeFormSnapshot();
  }, [formData, customFieldValues, isEditMode, serializeFormSnapshot]);

  useEffect(() => {
    if (!isEditMode || !client?.id) {
      baselineRef.current = null;
      pendingBaselineRef.current = false;
    }
  }, [client?.id, isEditMode]);

  const isDirty = useMemo(() => {
    if (!baselineRef.current) return false;
    return serializeFormSnapshot() !== baselineRef.current;
  }, [serializeFormSnapshot, formData, customFieldValues]);

  const validationErrorCount = useMemo(
    () => Object.values(errors).filter(Boolean).length,
    [errors]
  );

  // Desabilita o botão salvar se houver erros ou campos obrigatórios vazios
  const hasClientSideErrors = Object.values(errors).some(Boolean);
  const hasRequiredMissing = sections.some((section) => {
    if (!shouldRenderSection(section)) return false;

    return section.fields.some((field) => {
      if (!shouldRenderField(field)) return false;
      if (!field.required) return false;
      const value = formData[field.name];
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      );
    });
  });

  const disableSave =
    isSubmitting ||
    portalAccessSaving ||
    hasClientSideErrors ||
    hasRequiredMissing ||
    portalRequiresEmail ||
    (isPageVariant && isEditMode && !isDirty);

  useEffect(() => {
    const hint =
      validationErrorCount > 0
        ? `Revise ${validationErrorCount} campo${validationErrorCount > 1 ? "s" : ""} antes de salvar.`
        : portalRequiresEmail
          ? "Informe um e-mail para liberar o portal do cliente."
          : isPageVariant && isEditMode && !isDirty
            ? "Nenhuma alteração pendente."
            : "Suas alterações serão aplicadas ao perfil do cliente.";

    onSaveStateChange?.({
      disableSave,
      isSubmitting: isSubmitting || portalAccessSaving,
      isDirty,
      validationErrorCount,
      hint,
    });
  }, [
    disableSave,
    isSubmitting,
    portalAccessSaving,
    isDirty,
    validationErrorCount,
    portalRequiresEmail,
    isPageVariant,
    isEditMode,
    onSaveStateChange,
  ]);

  const handlePortalAcessoChange = async (v) => {
    const next = Number(v) ? 1 : 0;
    const prev = Number(formData.acesso_portal_cliente) ? 1 : 0;

    setFormData((formPrev) => ({ ...formPrev, acesso_portal_cliente: next }));
    setApiError(null);

    if (next && !String(formData.email || "").trim()) {
      setErrors((errPrev) => ({
        ...errPrev,
        email: "E-mail é obrigatório para liberar o portal do cliente.",
      }));
    } else {
      setErrors((errPrev) => {
        const nextErrors = { ...errPrev };
        if (
          nextErrors.email === "E-mail é obrigatório para liberar o portal do cliente."
        ) {
          nextErrors.email = "";
        }
        return nextErrors;
      });
    }

    if (!client?.id) return;

    setPortalAccessSaving(true);
    try {
      await fetchData(`/clients/${client.id}`, "PUT", { acesso_portal_cliente: next });
    } catch (e) {
      setFormData((formPrev) => ({ ...formPrev, acesso_portal_cliente: prev }));
      setApiError(e?.message || "Não foi possível atualizar o acesso ao portal.");
    } finally {
      setPortalAccessSaving(false);
    }
  };

  const quickFieldNames = new Set([
    "tipo",
    "nome",
    "apelido",
    "cpf",
    "razao_social",
    "cnpj",
    "email",
    "celular",
    "telefone",
    "whatsapp",
    "cidade",
    "uf",
    "id_projeto",
    "status",
  ]);

  const visibleFields = (section) => {
    if (formMode === "complete") return section.fields;
    return section.fields.filter((f) => quickFieldNames.has(f.name));
  };

  const formTabs = useMemo(() => {
    if (!isPageVariant) return [];
    return CLIENT_FORM_TAB_GROUPS.filter((tab) => {
      if (tab.includesPortal || tab.includesCustomFields) return true;
      return tab.sectionTitles.some((title) => {
        const section = sections.find((s) => s.title === title);
        if (!section || !shouldRenderSection(section)) return false;
        return visibleFields(section).filter(shouldRenderField).length > 0;
      });
    });
  }, [isPageVariant, sections, formData.tipo, formMode, fieldDefs?.client]);

  useEffect(() => {
    if (activeFormTab >= formTabs.length) {
      setActiveFormTab(Math.max(0, formTabs.length - 1));
    }
  }, [activeFormTab, formTabs.length]);

  const renderSectionFields = (section, { showTitle = false } = {}) => {
    if (!shouldRenderSection(section)) return null;
    const fieldsToRender = visibleFields(section).filter(shouldRenderField);
    if (!fieldsToRender.length) return null;

    return (
      <Box key={section.title} sx={{ mb: 2.5 }}>
        {showTitle ? (
          <Typography sx={clientFormSubsectionTitleSx}>{section.title}</Typography>
        ) : null}
        <Box sx={clientFormFieldsGridSx}>
          {fieldsToRender.map((field) => {
            if (field.name === "observacao") {
              return (
                <ClientObservacaoField
                  key="observacao"
                  value={formData.observacao ?? ""}
                  onChange={handleChange}
                  auditLines={observacaoAuditLines}
                  error={errors.observacao}
                />
              );
            }

            const span = clientFormFieldGridSpan(field);
            const fieldProps =
              field.name === "inscricao_estadual" && Number(formData.inscricao_estadual_isento)
                ? { ...field, disabled: true }
                : field;
            return (
              <Box
                key={field.name}
                sx={{ gridColumn: { xs: "1 / -1", sm: `span ${span}` }, minWidth: 0 }}
              >
                <ClientFormFieldControl
                  field={fieldProps}
                  label={fieldLabel(field)}
                  formData={formData}
                  errors={errors}
                  onChange={handleChange}
                  onCepChange={handleCepChange}
                  onCompanyChange={handleCompanyChange}
                  companies={companies}
                  companiesLoading={companiesLoading}
                  clientGroups={clientGroups}
                  groupsLoading={groupsLoading}
                  tagOptions={tagOptions}
                  onTagsChange={(next) => {
                    const cleaned = (next || [])
                      .map((t) => String(t || "").trim())
                      .filter(Boolean);
                    setFormData((prev) => ({ ...prev, tags: Array.from(new Set(cleaned)) }));
                  }}
                  onTagSearch={setTagSearch}
                  onGrupoChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      id_grupo: v === "" ? "" : Number(v),
                    }));
                    setApiError(null);
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const formShellSx = isPageVariant
    ? {
        width: "100%",
        maxWidth: "none",
        minWidth: 0,
        p: 0,
        bgcolor: "transparent",
        boxShadow: "none",
      }
    : {
        p: { xs: 2, sm: 2.5 },
        width: "100%",
        maxWidth: 960,
        mx: "auto",
      };

  const pageFormContent = (
    <Paper variant="outlined" sx={clientFormTabShellSx}>
      <Tabs
        value={activeFormTab}
        onChange={(_e, v) => setActiveFormTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={clientFormTabsSx}
      >
        {formTabs.map((tab) => (
          <Tab key={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={clientFormTabPanelSx}>
        {formTabs[activeFormTab]?.sectionTitles?.map((title) => {
          const section = sections.find((s) => s.title === title);
          if (!section) return null;
          const visibleCount = formTabs[activeFormTab].sectionTitles.filter((t) => {
            const s = sections.find((x) => x.title === t);
            return (
              s &&
              shouldRenderSection(s) &&
              visibleFields(s).filter(shouldRenderField).length > 0
            );
          }).length;
          return renderSectionFields(section, { showTitle: visibleCount > 1 });
        })}

        {formTabs[activeFormTab]?.includesCustomFields &&
        (fieldDefs?.client || []).some((d) => d.visible !== false) ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={clientFormSubsectionTitleSx}>Campos adicionais</Typography>
            <CustomFieldsBlock
              definitions={fieldDefs?.client || []}
              values={customFieldValues}
              onCommit={(key, val) =>
                setCustomFieldValues((prev) => ({ ...prev, [key]: val }))
              }
              loading={fieldDefsLoading}
            />
          </Box>
        ) : null}

        {formTabs[activeFormTab]?.includesPortal ? (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2.5 }} />
            <ClientPortalSection
              clientId={client?.id}
              email={formData.email}
              acessoPortal={Number(formData.acesso_portal_cliente)}
              hasPassword={Boolean(client?.hasPassword)}
              accessSaving={portalAccessSaving}
              onAcessoChange={handlePortalAcessoChange}
              embeddedInTab
            />
          </Box>
        ) : null}
      </Box>
    </Paper>
  );

  const formContent = (
    <>
      {isPageVariant ? pageFormContent : null}

      {!isPageVariant
        ? sections.map((section) => {
            if (!shouldRenderSection(section)) return null;
            const fieldsToRender = visibleFields(section).filter(shouldRenderField);
            if (!fieldsToRender.length) return null;
            return (
              <Card key={section.title} variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title={section.title} subheader={section.subtitle} sx={{ pb: 1 }} />
                <CardContent>
                  <Box sx={clientFormFieldsGridSx}>
                    {fieldsToRender.map((field) => {
                      if (field.name === "observacao") {
                        return (
                          <ClientObservacaoField
                            key="observacao"
                            value={formData.observacao ?? ""}
                            onChange={handleChange}
                            auditLines={observacaoAuditLines}
                            error={errors.observacao}
                          />
                        );
                      }
                      return (
                      <Box
                        key={field.name}
                        sx={{
                          gridColumn: {
                            xs: "1 / -1",
                            sm: `span ${clientFormFieldGridSpan(field)}`,
                          },
                          minWidth: 0,
                        }}
                      >
                        <ClientFormFieldControl
                          field={
                            field.name === "inscricao_estadual" &&
                            Number(formData.inscricao_estadual_isento)
                              ? { ...field, disabled: true }
                              : field
                          }
                          label={fieldLabel(field)}
                          formData={formData}
                          errors={errors}
                          onChange={handleChange}
                          onCepChange={handleCepChange}
                          onCompanyChange={handleCompanyChange}
                          companies={companies}
                          companiesLoading={companiesLoading}
                          clientGroups={clientGroups}
                          groupsLoading={groupsLoading}
                          tagOptions={tagOptions}
                          onTagsChange={(next) => {
                            const cleaned = (next || [])
                              .map((t) => String(t || "").trim())
                              .filter(Boolean);
                            setFormData((prev) => ({
                              ...prev,
                              tags: Array.from(new Set(cleaned)),
                            }));
                          }}
                          onTagSearch={setTagSearch}
                          onGrupoChange={(e) => {
                            const v = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              id_grupo: v === "" ? "" : Number(v),
                            }));
                            setApiError(null);
                          }}
                        />
                      </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        : null}

      {!isPageVariant &&
      (fieldDefs?.client || []).some((d) => d.visible !== false) ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Campos adicionais" sx={{ pb: 1 }} />
          <CardContent>
            <CustomFieldsBlock
              definitions={fieldDefs?.client || []}
              values={customFieldValues}
              onCommit={(key, val) =>
                setCustomFieldValues((prev) => ({ ...prev, [key]: val }))
              }
              loading={fieldDefsLoading}
            />
          </CardContent>
        </Card>
      ) : null}

      {!isPageVariant ? (
        <ClientPortalSection
          clientId={client?.id}
          email={formData.email}
          acessoPortal={Number(formData.acesso_portal_cliente)}
          hasPassword={Boolean(client?.hasPassword)}
          accessSaving={portalAccessSaving}
          onAcessoChange={handlePortalAcessoChange}
        />
      ) : null}

      {!hideFooterActions && !isPageVariant ? (
        <ClientFormActionsBar variant="embedded">
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={disableSave}>
            {isSubmitting ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </ClientFormActionsBar>
      ) : null}
    </>
  );

  return (
    <Paper
      component="form"
      id={formId}
      onSubmit={handleSubmit}
      elevation={isPageVariant ? 0 : 2}
      sx={formShellSx}
    >
      {!isPageVariant ? (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {client && client.id ? "Editar cliente" : "Adicionar cliente"}
          </Typography>
        </Box>
      ) : null}

      {!isEditMode ? (
        <Box sx={{ mb: isPageVariant ? 2 : 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={formMode}
            onChange={(_e, next) => {
              if (next) setFormMode(next);
            }}
          >
            <ToggleButton value="quick">Cadastro rápido</ToggleButton>
            <ToggleButton value="complete">Cadastro completo</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : null}

      {apiError ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{apiError}</Alert>
        </Box>
      ) : null}

      {formContent}
    </Paper>
  );
};

export default AddClientForm;
